import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { TARGET_COMMENT_ID_QUERY_PARAM } from 'shared/constants/comments'
import { AccessDenied, getAccessDeniedText } from 'shared/errors/access-denied'
import useUser from 'shared/hooks/use-user'
import { rollbar } from 'shared/services/rollbar'
import { CommentsList } from 'modules/comments/components'
import { CommentsTypeEnum } from 'modules/comments/types'
import { SEARCH_LECTURES_QUERY_PARAM } from '../consts/search-lectures'
import { useCompleteCourseLecture } from '../hooks/use-complete-course-lecture'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseLecture } from '../hooks/use-course-lecture'
import { useCourseLecturePreview } from '../hooks/use-course-lecture-preview'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useFixAnchorsForIframe } from '../hooks/use-fix-anchors-for-iframe'
import { useFixFontsForIframe } from '../hooks/use-fix-fonts-for-iframe'
import useStepRouter from '../hooks/use-step-router'
import { CourseAccessTypeEnum } from '../types/course-interface'
import {
  CourseLectureAccessDeniedDataInterface,
  CourseLectureId,
} from '../types/course-lecture-interface'
import { CourseQuizId } from '../types/course-quiz-interface'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'
import {
  applyHighlightStylesToIframe,
  removeHighlightStylesFromIframe,
} from '../utils/apply-highlight-styles-to-iframe'
import { resizeOnLoad, startFixLinksWatcher } from '../utils/iframe'
import { CourseContentSkeleton } from './course-content-skeleton'
import { CourseInfoScreenLayout } from './course-info-screens/course-info-screen-layout'
import { CoursePurchaseModuleScreen } from './course-info-screens/course-purchase-module-screen'
import { CourseLectureAssignment } from './course-lecture-assignment'
import { CourseStepButton } from './course-state-button'
import CourseTracker from './course-tracker'
import { ScrollSafeIframe } from './scroll-safe-iframe'
import { SecondaryButtonWithCustomTheme } from './secondary-button-with-custom-theme'

interface CourseLectureProps {
  setOpenPassQuiz: React.Dispatch<
    React.SetStateAction<false | { blockingStep: CourseLectureId | CourseQuizId }>
  >
  lectureId: CourseLectureId
  isPreview: boolean
  previewHash?: string
}

const tagsWithRightClickDisabled = ['VIDEO', 'AUDIO']

export const CourseLecture = ({
  setOpenPassQuiz,
  lectureId,
  isPreview,
  previewHash,
}: CourseLectureProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const {
    [TARGET_COMMENT_ID_QUERY_PARAM]: targetCommentId,
    [SEARCH_LECTURES_QUERY_PARAM]: searchQuery,
  } = router.query as {
    [TARGET_COMMENT_ID_QUERY_PARAM]?: string
    [SEARCH_LECTURES_QUERY_PARAM]?: string
  }

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const fixLinksCleanupRef = useRef<null | (() => void)>(null)

  const [iframeResized, setIframeResized] = useState(false)
  const [moduleLink, setModuleLink] = useState<string | null>(null)
  const [shouldSendLog, setShouldSendLog] = useState(true)
  const [accessError, setAccessError] = useState<string | null>(null)
  const { step, onNextStep } = useStepRouter()

  const { user } = useUser()
  const { completeCourseLecture, isFetching } = useCompleteCourseLecture()
  const { courseData, mutate: mutateCourseData } = useCourseData()
  const { courseMenu, mutate } = useCourseMenu({ courseId: courseData?.id })

  const { courseLecture, isValidating } = useCourseLecture({
    courseLectureId: courseMenu && lectureId,
    searchQuery,
    shouldFetch: !isPreview,
    onError: error => {
      if (
        error instanceof AccessDenied &&
        typeof error.response?.data === 'object' &&
        error.response?.data !== null &&
        'reason' in error.response?.data &&
        !('quiz' in error.response.data)
      ) {
        setAccessError(getAccessDeniedText(error.response?.data.reason))
      } else if (
        error instanceof AccessDenied &&
        error.response?.data &&
        'quiz' in error.response.data
      ) {
        setOpenPassQuiz({ blockingStep: error.response.data.quiz })
      } else if (error instanceof AccessDenied) {
        const data = error.response?.data as
          | CourseLectureAccessDeniedDataInterface
          | null
          | undefined
        if (data && 'module' in data) {
          // NOTE: sales page for module
          setModuleLink(data.module as string)
        }
      } else {
        toast.error(t('global.error'))
      }
      setShouldSendLog(false)
    },
  })
  const { courseLecturePreview } = useCourseLecturePreview({
    hash: previewHash,
    shouldRender: isPreview,
  })

  const loggingEnabled =
    !!courseData?.lectureSessionLogging &&
    !isValidating &&
    !!(courseLecture?.html || courseLecturePreview) &&
    shouldSendLog
  const highlightEnabled = !!searchQuery

  const syncHighlightStyles = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      if (!iframe) return

      if (!highlightEnabled) {
        removeHighlightStylesFromIframe(iframe)
        return
      }

      const rootStyles = getComputedStyle(document.documentElement)
      const highlightColor = rootStyles.getPropertyValue('--color-yellow').trim()

      if (!highlightColor) {
        rollbar.error('Failed to read Tailwind CSS Yellow var from :root')
      }

      applyHighlightStylesToIframe(iframe, highlightColor || '255 193 7')
    },
    [highlightEnabled],
  )

  const onCompleteCourseLecture = async () => {
    if (step?.id) {
      await completeCourseLecture(step.id)
      await mutate(
        data =>
          data?.map(module => ({
            ...module,
            lectures: module.lectures.map(lecture =>
              lecture.id === step?.id ? { ...lecture, completed: true } : lecture,
            ),
            quiz:
              module.quiz &&
              module.lectures.every(lecture => lecture.completed || lecture.id === step.id)
                ? { ...module.quiz, available: true }
                : module.quiz,
          })),
        false,
      )
      mutateCourseData()
      onNextStep()
    }
  }

  const disableRightClick = useCallback(() => {
    if (!iframeRef.current?.contentDocument?.body) return

    iframeRef.current.contentDocument.body.addEventListener('contextmenu', e => {
      const element = e.target as HTMLElement
      if (tagsWithRightClickDisabled.includes(element.tagName)) {
        e.preventDefault()
      }
    })
  }, [])

  useEffect(() => {
    if (courseData?.disableRightClick && iframeResized) {
      disableRightClick()
    }
  }, [courseData?.disableRightClick, disableRightClick, iframeResized])

  useEffect(() => {
    setModuleLink(null)
  }, [step?.id])

  useLayoutEffect(() => {
    const receiveMessage = (event: any) => {
      const shouldUpdateHeight =
        iframeRef.current &&
        iframeRef.current.contentDocument?.body &&
        event?.data?.type === 'lecture_content_height' &&
        event.data.height > 0

      if (shouldUpdateHeight) {
        iframeRef.current!.style.height = `${event.data.height}px`
        iframeRef.current!.style.position = 'relative'
        iframeRef.current!.style.opacity = '1'
        iframeRef.current!.contentDocument!.body.style.overflowY = 'hidden'
        setIframeResized(true)
      }
    }

    window.addEventListener('message', receiveMessage)
    return () => window.removeEventListener('message', receiveMessage)
  }, [])

  useFixFontsForIframe({ iframeRef })
  useFixAnchorsForIframe(iframeRef, iframeResized)

  useEffect(() => {
    return () => {
      // disconnect links watcher on unmount
      fixLinksCleanupRef.current?.()
    }
  }, [])

  useEffect(() => {
    syncHighlightStyles(iframeRef.current)
  }, [syncHighlightStyles])

  const handleLoadIframe = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = event.currentTarget

      const height = resizeOnLoad(iframe)
      setIframeResized(!!height)

      fixLinksCleanupRef.current?.()
      fixLinksCleanupRef.current = null

      fixLinksCleanupRef.current = startFixLinksWatcher(iframe)

      syncHighlightStyles(iframe)
    },
    [syncHighlightStyles],
  )

  const displayPurchaseScreen =
    !isValidating &&
    !!step &&
    ((!step.available && courseData?.dominantAccessType === CourseAccessTypeEnum.PartialAccess) ||
      moduleLink)

  if (displayPurchaseScreen) return <CoursePurchaseModuleScreen moduleLink={moduleLink} />

  const isLoading = (isValidating || !(courseLecture?.html || courseLecturePreview)) && !accessError

  if (isLoading) return <CourseContentSkeleton />

  if (accessError)
    return (
      <CourseInfoScreenLayout
        text={<span className="whitespace-pre-line">{t(accessError)}</span>}
      />
    )

  return (
    <div
      className={`relative w-full ${addPublicCustomClassNamePrefix('course-lecture__container')}`}
    >
      {loggingEnabled && <CourseTracker />}
      <div
        className={`relative w-full overflow-y-auto rounded-md bg-THEME-block-background [&>*::-webkit-scrollbar]:hidden`}
      >
        <ScrollSafeIframe
          srcDoc={courseLecture?.html || courseLecturePreview}
          ref={iframeRef}
          onLoad={handleLoadIframe}
        />
        {iframeResized && user && courseLecture?.showComments && (
          <div
            className={`px-3 py-4 lg:px-4 lg:py-5 ${addPublicCustomClassNamePrefix('course-lecture__comments-container')}`}
          >
            <CommentsList
              commentsType={CommentsTypeEnum.Page}
              onError={() => toast.error(t('comments.error.error-loading-comments'))}
              course={{ pageId: courseLecture?.pageId }}
              targetCommentId={targetCommentId ? Number(targetCommentId) : undefined}
            />
          </div>
        )}
        {!iframeResized && (
          <CourseContentSkeleton
            className={`z-40 ${addPublicCustomClassNamePrefix('course__content-skeleton--lecture')}`}
          />
        )}
      </div>
      {iframeResized && (!!courseLecture?.assignment || !!courseLecture?.assigmentTitle) && (
        <CourseLectureAssignment
          lectureId={lectureId}
          courseId={courseData?.id}
          assignment={courseLecture.assignment}
          assigmentTitle={courseLecture.assigmentTitle}
          assignmentDescription={courseLecture.assigmentDescription}
          searchQuery={searchQuery}
        />
      )}
      {iframeResized && (
        <div className="mt-5 flex justify-center rounded-md bg-THEME-block-background p-6">
          {step?.completed ? (
            <CourseStepButton />
          ) : (
            <SecondaryButtonWithCustomTheme
              isLoading={isFetching}
              disabled={isPreview}
              className={`font-bold ${addPublicCustomClassNamePrefix('course__button--mark-as-completed')}`}
              onClick={onCompleteCourseLecture}
            >
              {t('course.mark_step_as_completed_button_label')}
            </SecondaryButtonWithCustomTheme>
          )}
        </div>
      )}
    </div>
  )
}
